import { AEMLink, NavigationRouteProps } from '@msc-b2c/interfaces/types';
import { replacePlaceholders, store } from '@msc-b2c/shared-utils';
import { NavigateFunction, NavigateOptions } from 'react-router-dom';

/**
 * Updates the copyright year placeholder in the footer/legal copyright text
 * and reveals the surrounding separators once the year has been resolved.
 *
 * Moved here from `@msc-b2c/editorial-components` because it is only used
 * by the my-msc navigation menu, avoiding an unnecessary dependency on the
 * editorial-components barrel (and its transitive gsap/parallax code) from
 * this app.
 */
export function updateCopyrightYear(): void {
  const copyrightElement = document.querySelector('.terms-conditions-privacy-copyright .cmp-text');
  const copyrightElementLight = document.querySelector('.-footer-copyright .cmp-text');

  if (copyrightElement) {
    let copyrightText = copyrightElement?.innerHTML;
    const currentYear = new Date().getFullYear();
    copyrightText = replacePlaceholders(copyrightText, {
      year: currentYear.toString() ?? '',
    });
    copyrightElement.innerHTML = copyrightText;
  }
  if (copyrightElement || copyrightElementLight) {
    const loaderElement = document.querySelector('.loader');
    if (loaderElement) {
      const loaderSeparators = loaderElement.parentElement?.querySelectorAll('.separator');
      loaderSeparators?.forEach((separator) => separator.classList.add('hide'));
    }
  }
}

/**
 * Calculates the final route based on the base route and target route.
 *
 * This function adjusts the target route depending on whether the base route
 * includes a `.html` extension (local environment) or not (testing environment).
 *
 * @param {string} baseRoute - The base route to compare against (e.g., `/plan-my-cruise.html`).
 * @param {string} targetRoute - The target route to be adjusted (e.g., `/plan-my-cruise/some-page`).
 * @returns {string} - The calculated route based on the environment and input routes.
 */
export const calculateRoute = (baseRoute: string, targetRoute: string): string => {
  // Check if the target route contains the base route (excluding `.html` if present)
  if (targetRoute.includes(baseRoute.replace('.html', ''))) {
    // Handle the case where the base route includes `.html` (local environment)
    if (baseRoute.includes('.html')) {
      // Remove `.html` from the base route
      const baseRouteWithoutHtml = baseRoute.replace('.html', ''); // e.g., `/plan-my-cruise`
      // Remove the base route from the target route
      const targetRouteWithoutBase = targetRoute.replace(baseRouteWithoutHtml, '');
      // Return the base route concatenated with the remaining part of the target route
      return baseRoute + targetRouteWithoutBase;
    } else {
      // For testing environment, return the target route as is
      return targetRoute;
    }
  }
  // If the target route does not include the base route, return the base route
  return baseRoute;
};

/**
 * Retrieves the parent route from the navigation items based on the current route.
 *
 * This function checks if there is at least one `navigationItem.url` where the `currentRoute`
 * is included. If found, it returns the `url` of the parent navigation item.
 *
 * @param {NavigationRouteProps[]} navigationItems - The list of navigation items to search through.
 * @param {string} currentRoute - The current route to match against the navigation items.
 * @returns {string} - The URL of the parent route if found, otherwise an empty string.
 */
export const getParentRoute = (
  navigationItems: NavigationRouteProps[],
  currentRoute: string
): string => {
  const parentRoute = navigationItems.find((navItem) => {
    return currentRoute
      .replace('.html', '')
      ?.includes(navItem.urlLink?.url?.replace('.html', '') ?? '');
  });
  return parentRoute?.urlLink?.url ?? '';
};

/**
 * Determines the route to navigate to based on the current route and navigation items.
 *
 * This function identifies the parent route using `getParentRoute` and removes it
 * from the `currentRoute`. It also removes any `.html` extension from the resulting route.
 *
 * @param {NavigationRouteProps[]} navigationItems - The list of navigation items to search through.
 * @param {string} currentRoute - The current route to process.
 * @returns {string} - The calculated route to navigate to.
 */
export const getRouteToNavigate = (
  navigationItems: NavigationRouteProps[],
  currentRoute: string
): string => {
  const parentRoute = getParentRoute(navigationItems, currentRoute);
  return currentRoute?.replace(parentRoute?.replace('.html', '') ?? '', '')?.replace('.html', '');
};

/**
 * Handles navigation or redirection based on the provided route.
 *
 * If the route is valid:
 * - Retrieves navigation items from the store.
 * - Determines the parent route and the route to navigate or redirect to.
 * - If the current URL includes the parent route (ignoring `.html`), uses the `navigate` function for client-side navigation.
 * - Otherwise, performs a full page reload to the target route.
 *
 * @param {AEMLink} route - The route object containing navigation information.
 * @param {NavigateFunction} navigate - The navigation function from `react-router-dom`.
 * @param options
 */
export const navigateOrRedirectToRoute = (
  route: AEMLink,
  navigate: NavigateFunction,
  options?: NavigateOptions
) => {
  if (route?.valid) {
    const navigationItems = store.getState().navigationMenuData.navigationItems;
    const parentRoute = getParentRoute(navigationItems || [], route?.url ?? '');
    const routeToNavigateOrRedirect = getRouteToNavigate(navigationItems || [], route?.url ?? '');
    if (globalThis.location.href?.includes(parentRoute.replace('.html', ''))) {
      navigate(
        `${routeToNavigateOrRedirect}${globalThis.location.href.includes('.html') ? '.html' : ''}`,
        options
      );
    } else {
      globalThis.location.href =
        parentRoute + routeToNavigateOrRedirect + (parentRoute.includes('.html') ? '.html' : '');
    }
  }
};
